import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useConfig } from "@/lib/config";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, MicrosoftOutlookLogo, LockKey, Flask, ShieldCheck, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import GkLogo from "@/components/GkLogo";
import PoweredBy from "@/components/PoweredBy";

export default function Login() {
  const { user, login, signup, previewLogin } = useAuth();
  const { preview_mode, microsoft_configured, self_signup_enabled, approved_email_domain } = useConfig();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await signup(email, password, name);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  const fillDemo = (kind) => {
    if (kind === "manager") { setEmail("manager@asktracey.com"); setPassword("manager123"); }
    else { setEmail("team@asktracey.com"); setPassword("team123"); }
    setMode("login");
  };

  const enterPreview = async (role) => {
    setBusy(true);
    try {
      await previewLogin(role);
      toast.info("Preview mode — no real emails will be sent and Google Drive is not connected.");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview mode is unavailable");
    } finally { setBusy(false); }
  };

  const startMicrosoft = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/microsoft/login");
      if (data?.authorize_url) window.location.href = data.authorize_url;
      else toast.error("Microsoft sign-in did not return an authorize URL");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Microsoft sign-in is unavailable");
    } finally { setBusy(false); }
  };

  const showPasswordForm = preview_mode || self_signup_enabled;

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-canvas">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-surface border-r border-line">
        <GkLogo variant="horizontal" className="w-64 h-auto object-contain -ml-4" />

        <div className="max-w-lg">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gk-red mb-6">
            <span className="w-8 h-px bg-gk-red" /> Internal knowledge assistant
          </div>
          <h1 className="font-heading text-6xl leading-[0.98] text-gk-charcoal">
            Ask <span className="text-gk-red">Tracey</span> —
            <span className="block text-ink-soft italic mt-2">Tracey's playbook, on demand.</span>
          </h1>
          <p className="mt-6 text-ink-soft text-base max-w-md">
            An internal knowledge assistant for The GK Team at Signature, grounded only in Tracey's approved training materials.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 text-xs text-ink-soft">
            <LockKey size={14} weight="duotone" />
            Team members have read-only access. Managers curate the knowledge base.
          </div>
          <div className="pt-4 border-t border-line/70">
            <PoweredBy variant="login" />
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <GkLogo variant="stacked" className="w-48 h-auto object-contain -ml-2" />
          </div>

          <h2 className="font-heading text-4xl text-gk-charcoal">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="text-ink-soft mt-2 mb-8">
            {mode === "login"
              ? "Sign in with your GK Team email to ask Tracey anything."
              : "Use your GK Team email to get started."}
          </p>

          <button
            type="button"
            data-testid="ms-signin"
            disabled={busy}
            onClick={microsoft_configured
              ? startMicrosoft
              : () => toast.info(`Microsoft sign-in is not yet configured. Once activated, only ${approved_email_domain} accounts will be accepted.`)}
            className="w-full h-11 rounded-full border border-line bg-white hover:border-gk-charcoal hover:-translate-y-[1px] transition-all flex items-center justify-center gap-2 text-sm font-medium text-gk-charcoal disabled:opacity-60"
          >
            <MicrosoftOutlookLogo size={18} weight="fill" className="text-[#0078D4]" />
            {microsoft_configured ? "Continue with Microsoft / Outlook" : "Continue with Microsoft / Outlook (not yet configured)"}
          </button>
          <div className="text-[11px] text-ink-soft text-center mt-2">
            {microsoft_configured
              ? `Only @${approved_email_domain} accounts are accepted.`
              : `Production sign-in for @${approved_email_domain} accounts — activation pending.`}
          </div>

          {showPasswordForm && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-line" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">or</span>
                <div className="flex-1 h-px bg-line" />
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
                {mode === "signup" && (
                  <div>
                    <Label htmlFor="name">Full name</Label>
                    <Input id="name" data-testid="signup-name" value={name} onChange={(e)=>setName(e.target.value)} required className="mt-1 h-11" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">GK Team email</Label>
                  <Input id="email" type="email" data-testid="auth-email" value={email} onChange={(e)=>setEmail(e.target.value)}
                    placeholder="you@gkpropertieslv.com" required className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" data-testid="auth-password" value={password} onChange={(e)=>setPassword(e.target.value)} required className="mt-1 h-11" />
                </div>

                <Button type="submit" disabled={busy} data-testid="auth-submit"
                  className="w-full h-11 bg-gk-red hover:bg-gk-red-bright text-white rounded-full group">
                  {busy ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")}
                  <ArrowRight className="ml-2 transition-transform group-hover:translate-x-0.5" size={18} weight="bold" />
                </Button>
              </form>
            </>
          )}

          {showPasswordForm && (
            <div className="mt-6 flex items-center justify-between text-sm">
              <button className="text-ink-soft hover:text-gk-charcoal underline underline-offset-4"
                onClick={()=>setMode(mode==="login"?"signup":"login")} data-testid="auth-toggle">
                {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
              </button>
            </div>
          )}

          <div className="mt-10 p-4 rounded-2xl border border-line bg-surface/70" hidden={!preview_mode}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-2">Demo accounts</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button data-testid="fill-manager" onClick={()=>fillDemo("manager")}
                className="flex-1 text-left px-3 py-2 rounded-xl bg-white border border-line hover:border-gk-red transition-colors">
                <div className="text-sm font-medium text-gk-charcoal">Manager</div>
                <div className="text-xs text-ink-soft">manager@asktracey.com</div>
              </button>
              <button data-testid="fill-team" onClick={()=>fillDemo("team")}
                className="flex-1 text-left px-3 py-2 rounded-xl bg-white border border-line hover:border-gk-red transition-colors">
                <div className="text-sm font-medium text-gk-charcoal">Team</div>
                <div className="text-xs text-ink-soft">team@asktracey.com</div>
              </button>
            </div>
          </div>

          {preview_mode && (
            <div className="mt-6 p-4 rounded-2xl border-2 border-dashed border-gk-red/60 bg-gk-red/5" data-testid="preview-panel">
              <div className="flex items-center gap-2 mb-1">
                <Flask size={16} weight="fill" className="text-gk-red" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-gk-red font-semibold">Preview mode</div>
              </div>
              <p className="text-sm text-gk-charcoal">
                Enter the app without Microsoft/Outlook SSO. This bypass only runs when
                <span className="font-mono text-[11px] px-1.5 py-0.5 mx-1 rounded bg-white border border-line">PREVIEW_MODE=true</span>
                on the server — turn it off before publishing.
              </p>
              <p className="text-xs text-ink-soft mt-1">No real manager emails will be sent and the live Google Drive folder is not connected.</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button onClick={()=>enterPreview("manager")} disabled={busy} data-testid="preview-manager"
                  className="w-full h-11 bg-gk-red hover:bg-gk-red-bright text-white rounded-full">
                  <ShieldCheck size={16} weight="fill" className="mr-1.5" /> Preview as Manager
                </Button>
                <Button onClick={()=>enterPreview("team")} disabled={busy} data-testid="preview-team"
                  variant="outline"
                  className="w-full h-11 rounded-full border-gk-red text-gk-red hover:bg-gk-red hover:text-white">
                  <UsersThree size={16} weight="fill" className="mr-1.5" /> Preview as Team member
                </Button>
              </div>
            </div>
          )}

          {/* Mobile-only credit — visible when the left brand panel is hidden */}
          <div className="mt-10 pt-6 border-t border-line/70 lg:hidden">
            <PoweredBy variant="login" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
